'use client'

import { Container, FlexContainer, Footer, LineSeparator, LineSeparatorVariant } from '@news12/storybook-news'

import { useEffect, useMemo } from 'react'

import normalizeFooter from '../../data/normalizers/footer/normalizeFooter'
import { NavigationFooter } from '../../data/transformers/extractNavigation'
import useBelowBreakpoint from '../../hooks/useBelowBreakpoint'
import useIsProd from '../../hooks/useIsProd'
import { AD_UNIT_SIZES } from '../../utils/ads/adConfig'
import AdUnit from '../Ads/AdUnit'
import { StyledAdFooterContainer } from '../Ads/Ads.styles'
import { useAdsContext } from '../AdsProvider'
import { useEnvironmentContext } from '../EnvironmentProvider'

export type FooterField = {
  path: string
  isRoot?: boolean
  name: string
}

export type FooterSectionProps = {
  footer: NavigationFooter
}

const FooterSection = ({ footer }: FooterSectionProps) => {
  const isMobile = useBelowBreakpoint('sm')
  const isTablet = useBelowBreakpoint('lg')
  const { categoriesNames, adsEnabled, sponsorshipAdsEnabled, dvdPreRollEnabled } = useAdsContext()
  const { bottomOptions, topLists } = useMemo(() => normalizeFooter(footer), [footer])

  const setupPrivacyManagerLink = () => {
    try {
      const privacyManagerItems = Array.from(document.querySelectorAll('li')).filter(
        x => x.getAttribute('value') === 'Privacy Manager'
      )

      if (!privacyManagerItems.length) return

      const anchor = privacyManagerItems[0]?.children[0] as HTMLAnchorElement
      if (!anchor) return

      const firstChild = anchor.children[0]
      if (firstChild) {
        firstChild.setAttribute('id', 'pmLink')
      }

      anchor.removeAttribute('href')
      anchor.onclick = () => {}
      anchor.addEventListener('click', e => {
        e.preventDefault()
      })
    } catch (error) {
      console.error('Error setting up Privacy Manager link:', error)
    }
  }

  useEffect(() => {
    setupPrivacyManagerLink()
  }, [])

  // We show the custom ad unit only on the production due to the Gumgum ad behavior on the stage
  // Gumgum ads appear and overlay the content, which makes the stage environment testing tricky
  const isProd = useIsProd()
  const { customAdDisabled } = useEnvironmentContext()
  const isCustomAdAvailable = adsEnabled && isProd && dvdPreRollEnabled && !customAdDisabled

  const desktopAdUnitSizes = useMemo(
    () => ({
      maxWidth: '728px'
    }),
    []
  )

  return (
    <>
      <footer className='main-footer redesigned-footer'>
        <Container>
          <LineSeparator variant={LineSeparatorVariant.Strong} />
          <Footer
            topLists={topLists}
            bottomOptions={bottomOptions}
            homeUrl='/'
            externalLinks={{
              appStoreUrl: 'https://apps.apple.com/us/app/news-12-mobile/id444268184',
              googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.sentinelbd.news12&hl=en_US'
            }}
          />
        </Container>
        {sponsorshipAdsEnabled && !isTablet && (
          <StyledAdFooterContainer>
            <FlexContainer justifyContent='center'>
              <AdUnit
                adUnitTemplate='leaderboard'
                adUnitSizes={desktopAdUnitSizes}
                contentfulCategories={categoriesNames}
                id='ad-desktop-footer'
                type='footerDesktopSponsorship'
              />
            </FlexContainer>
          </StyledAdFooterContainer>
        )}
        {adsEnabled && isMobile && (
          <StyledAdFooterContainer>
            <FlexContainer justifyContent='center'>
              <AdUnit
                adUnitTemplate='leaderboard'
                adUnitSizes={AD_UNIT_SIZES}
                contentfulCategories={categoriesNames}
                id='ad-mobile-footer'
                type='footerRedesign'
              />
            </FlexContainer>
          </StyledAdFooterContainer>
        )}
      </footer>
      {isCustomAdAvailable && <AdUnit contentfulCategories={categoriesNames} id='ad-custom' type='custom' />}
    </>
  )
}

export default FooterSection
